/**
 * SettingsTab — a source's identity (read-only), its active switch, and the remove action.
 * Template example: NextAdmin `profile/notification/page.tsx` (a setting with a toggle), `profile/account/page.tsx`
 * (danger zone); demo `/profile`.
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import type { IntelSource } from '../../../types/intel-source.js';
import { zIntelSourceUpdate } from '../../../generated/api/zod.gen.js';
import {
  deleteIntelSourceMutation,
  updateIntelSourceMutation,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { Can } from '../../../components/auth/Can.js';
import { Button, Card, ConfirmDialog, SettingList, SettingRow, useToast } from '../../../components/ui';
import { Form, SubmitButton, SwitchFormField, useZodForm } from '../../../components/form';
import { useIntelSourceInvalidation } from './intel-source-queries.js';

interface SettingsTabProps {
  source: IntelSource;
  canManage: boolean;
  onUpdated: () => void;
  onClose: () => void;
}

const settingsSchema = zIntelSourceUpdate.pick({ active: true });

export const SettingsTab: React.FC<SettingsTabProps> = ({ source, canManage, onUpdated, onClose }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const invalidate = useIntelSourceInvalidation();
  const { notify } = useToast();
  const form = useZodForm(settingsSchema, { defaultValues: { active: source.active } });

  const saveMutation = useMutation({
    ...updateIntelSourceMutation(),
    onSuccess: () => {
      invalidate(source.id);
      onUpdated();
      notify('Source settings saved.', 'success');
    },
  });

  const deleteMutation = useMutation({
    ...deleteIntelSourceMutation(),
    onSuccess: () => {
      invalidate();
      onUpdated();
      setConfirmDelete(false);
      onClose();
    },
    onError: () => notify('Could not remove the source. Try again.', 'danger'),
  });

  return (
    <div className="max-w-3xl space-y-5">
      <Card title="Source settings">
        <dl className="mb-5 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-muted">Handle</dt>
            <dd className="mt-0.5 font-mono text-xs text-body">@{source.handle}</dd>
          </div>
          <div>
            <dt className="text-muted">Platform</dt>
            <dd className="mt-0.5 text-body capitalize">{source.platform}</dd>
          </div>
          <div>
            <dt className="text-muted">Category</dt>
            <dd className="mt-0.5 text-body">{source.category.replace(/_/g, ' ')}</dd>
          </div>
        </dl>
        <Form
          form={form}
          onSubmit={(values) => saveMutation.mutateAsync({ path: { id: source.id }, body: { active: values.active } })}
        >
          <SwitchFormField
            name="active"
            label="Active"
            description="An inactive source is skipped when content is fetched."
            disabled={!canManage}
          />
          <Can policy="CanManageAgents">
            <div>
              <SubmitButton variant="primary" size="md">
                Save changes
              </SubmitButton>
            </div>
          </Can>
        </Form>
      </Card>

      <Can policy="CanManageAgents">
        <SettingList card>
          <SettingRow
            title="Remove source"
            description="Deletes the source and the content collected from it. This cannot be undone."
          >
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} aria-hidden="true" /> Remove source
            </Button>
          </SettingRow>
        </SettingList>
        <ConfirmDialog
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          onConfirm={() => deleteMutation.mutate({ path: { id: source.id } })}
          title="Remove this source?"
          message={`Remove ${source.name ?? source.handle} and everything collected from it? This cannot be undone.`}
          confirmLabel="Remove source"
          loading={deleteMutation.isPending}
        />
      </Can>
    </div>
  );
};
